import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertCircle,
  Book,
  Pencil,
  Stethoscope,
  TrendingUp,
} from "tabler-icons-react";
import { useGetClassesQuery } from "../../../redux/features/classeApi";
import { useGetActivitiesGlobalStatsQuery } from "../../dasboard/api/features/queriesApi";

const COLUMN_CONFIG = [
  {
    key: "DIAGNOSTIC_LECTURE",
    label: "Diagnostic Lecture",
    color: "teal",
    icon: Stethoscope,
    getBgColor: (pct) => `rgba(16, 185, 129, ${0.4 + (pct / 100) * 0.6})`,
  },
  {
    key: "DIAGNOSTIC_MATHS",
    label: "Diagnostic Maths",
    color: "orange",
    icon: Stethoscope,
    getBgColor: (pct) => `rgba(249, 115, 22, ${0.4 + (pct / 100) * 0.6})`,
  },
  {
    key: "LESSON_QUIZ",
    label: "Vérifier la leçon",
    color: "blue",
    icon: Book,
    getBgColor: (pct) => `rgba(59, 130, 246, ${0.4 + (pct / 100) * 0.6})`,
  },
  {
    key: "EXERCISE_CORRECTION",
    label: "Corriger l'exercice",
    color: "dark",
    icon: Pencil,
    getBgColor: (pct) => `rgba(30, 64, 175, ${0.4 + (pct / 100) * 0.6})`,
  },
];

// Config of categories (funnel steps)
const CATEGORIES = [
  {
    key: "USER",
    label: "User",
    subtext: "parents",
  },
  {
    key: "USER_ACTIVE",
    label: "User Activé",
    subtext: "parents",
  },
  {
    key: "CLIENT",
    label: "Client",
    subtext: "parents",
    badge: "a payé",
  },
  {
    key: "CLIENT_ENGAGE",
    label: "Client engagé",
    subtext: "parents",
    badge: "+ actif 7j",
  },
  {
    key: "CLIENT_ACTIF",
    label: "Client actif",
    subtext: "parent",
    badge: "habitude installée",
  },
  {
    key: "CLIENT_IMPACTE",
    label: "Client impacté",
    subtext: "",
    badge: "grisé V1",
    disabled: true,
  },
];

const BASE_GRADES = [
  "CI",
  "CP",
  "CE1",
  "CE2",
  "CM1",
  "CM2",
  "Collège",
  "Lycée",
  "Préscolaire",
];

// Helper to query and aggregate results for a category
function StatsRow({
  category,
  activeClassIds,
  startDate,
  endDate,
  activeFunnelCounts,
  onDataLoaded,
}) {
  const [classDataMap, setClassDataMap] = useState({});

  // Reset when class IDs or dates change
  useEffect(() => {
    setClassDataMap({});
  }, [activeClassIds, startDate, endDate]);

  const handleDataLoaded = (id, data) => {
    setClassDataMap((prev) => ({ ...prev, [id]: data }));
  };

  // If "Toutes" (activeClassIds empty)
  const isAll = activeClassIds.length === 0;

  // Single global query for Toutes
  const { data: globalData, isLoading: isGlobalLoading } =
    useGetActivitiesGlobalStatsQuery(
      { start_date: startDate, end_date: endDate, category: category.key },
      { skip: !isAll },
    );

  const aggregated = useMemo(() => {
    if (isAll) {
      return globalData;
    }

    // Combine data from classDataMap
    let total_activities = 0;
    const activities_by_type = {};

    activeClassIds.forEach((id) => {
      const data = classDataMap[id];
      if (!data) return;

      total_activities += data.total_activities || 0;
      const byType = data.activities_by_type || {};

      Object.keys(byType).forEach((type) => {
        if (!activities_by_type[type]) {
          activities_by_type[type] = {
            name: byType[type].name,
            count: 0,
            percentage: 0,
          };
        }
        activities_by_type[type].count += byType[type].count || 0;
      });
    });

    // Recompute percentages
    Object.keys(activities_by_type).forEach((type) => {
      const item = activities_by_type[type];
      item.percentage =
        total_activities > 0
          ? parseFloat(((item.count / total_activities) * 100).toFixed(2))
          : 0;
    });

    return {
      total_activities,
      activities_by_type,
    };
  }, [isAll, globalData, classDataMap, activeClassIds]);

  useEffect(() => {
    if (aggregated && onDataLoaded) {
      onDataLoaded(category.key, aggregated);
    }
  }, [aggregated, category.key, onDataLoaded]);

  const isRowLoading = useMemo(() => {
    if (isAll) return isGlobalLoading;
    // Loading if not all active class IDs have loaded data in classDataMap
    return activeClassIds.some((id) => !classDataMap[id]);
  }, [isAll, isGlobalLoading, activeClassIds, classDataMap]);

  // Render hidden fetchers to load RTK Query data
  const fetchers = !isAll
    ? activeClassIds.map((id) => (
        <ClassDataFetcher
          key={`${id}-${startDate}-${endDate}-${category.key}`}
          classeId={id}
          categoryKey={category.key}
          startDate={startDate}
          endDate={endDate}
          onLoaded={(data) => handleDataLoaded(id, data)}
        />
      ))
    : null;

  const parentCount = activeFunnelCounts[category.key] || 0;

  return (
    <>
      {fetchers}
      <tr
        style={{
          borderBottom: "1px solid #e9ecef",
          opacity: category.disabled ? 0.5 : 1,
          backgroundColor: category.disabled ? "#f8f9fa" : "transparent",
        }}
      >
        {/* Row Header */}
        <td style={{ padding: "16px 20px" }}>
          <Box>
            <Text
              weight={700}
              size="sm"
              color={category.disabled ? "dimmed" : "dark"}
            >
              {category.label}
            </Text>
            <Group spacing={6} mt={2}>
              <Text size="xs" color="dimmed" weight={500}>
                {category.disabled ? "—" : `${parentCount} ${category.subtext}`}
              </Text>
              {category.badge && (
                <Badge
                  color={
                    category.disabled
                      ? "gray"
                      : category.key.includes("CLIENT")
                        ? "green"
                        : "blue"
                  }
                  size="xs"
                  variant="light"
                  styles={{
                    label: { fontSize: "10px", textTransform: "none" },
                  }}
                >
                  {category.badge}
                </Badge>
              )}
            </Group>
          </Box>
        </td>

        {/* Feature Columns */}
        {COLUMN_CONFIG.map((col) => {
          const typeData = aggregated?.activities_by_type?.[col.key];
          const count = typeData?.count || 0;
          const percentage = typeData?.percentage || 0;

          if (isRowLoading) {
            return (
              <td
                key={col.key}
                style={{ padding: "12px", textAlign: "center" }}
              >
                <Loader size="xs" />
              </td>
            );
          }

          if (category.disabled) {
            return (
              <td
                key={col.key}
                style={{
                  padding: "16px",
                  textAlign: "center",
                  backgroundColor: "#f8f9fa",
                  color: "#adb5bd",
                }}
              >
                <Text size="sm" weight={700}>
                  —
                </Text>
              </td>
            );
          }

          return (
            <td
              key={col.key}
              style={{
                padding: "12px 16px",
                textAlign: "center",
                backgroundColor:
                  count > 0 ? col.getBgColor(percentage) : "#f8f9fa",
                color: count > 0 ? "#ffffff" : "#adb5bd",
                transition: "background-color 0.2s ease",
              }}
            >
              {count > 0 ? (
                <Box>
                  <Text weight={800} size="md">
                    {Math.round(percentage)}%
                  </Text>
                  <Text
                    size="xs"
                    weight={600}
                    style={{ opacity: 0.85, marginTop: 2 }}
                  >
                    {count} act.
                  </Text>
                </Box>
              ) : (
                <Box>
                  <Text size="sm" weight={700} color="dimmed">
                    —
                  </Text>
                  <Text
                    size="xs"
                    color="dimmed"
                    weight={500}
                    style={{ marginTop: 2 }}
                  >
                    0
                  </Text>
                </Box>
              )}
            </td>
          );
        })}

        {/* Total Column */}
        <td
          style={{
            padding: "16px",
            textAlign: "center",
            backgroundColor: category.disabled ? "#e9ecef" : "#1a202c",
            color: "#ffffff",
            width: "100px",
          }}
        >
          {isRowLoading ? (
            <Loader size="xs" color="white" />
          ) : (
            <Text weight={800} size="sm">
              {category.disabled ? "—" : aggregated?.total_activities || 0}
            </Text>
          )}
        </td>
      </tr>
    </>
  );
}

// Inner helper component to run the query for a single class ID
function ClassDataFetcher({
  classeId,
  categoryKey,
  startDate,
  endDate,
  onLoaded,
}) {
  const { data } = useGetActivitiesGlobalStatsQuery({
    start_date: startDate,
    end_date: endDate,
    classe_id: classeId,
    category: categoryKey,
  });

  useEffect(() => {
    if (data) {
      onLoaded(data);
    }
  }, [data, onLoaded]);

  return null;
}

export default function FunnelFeatureStats({
  dateRange,
  periodFilter,
  activeFunnelCounts,
  selectedGrades,
  setSelectedGrades,
}) {
  const [categoryDataMap, setCategoryDataMap] = useState({});

  const handleCategoryDataLoaded = useCallback((key, data) => {
    setCategoryDataMap((prev) => {
      if (JSON.stringify(prev[key]) === JSON.stringify(data)) return prev;
      return { ...prev, [key]: data };
    });
  }, []);

  const startDate = dateRange.start.format("YYYY-MM-DD");
  const endDate = dateRange.end.format("YYYY-MM-DD");

  // Fetch classes
  const { data: classesData, error: errorClasses } = useGetClassesQuery();

  // Find all active class IDs based on selected grades
  const activeClassIds = useMemo(() => {
    if (selectedGrades.size === 0) return [];
    return (classesData?.data || [])
      .filter((cls) => {
        return Array.from(selectedGrades).some((grade) =>
          cls.name.toUpperCase().includes(grade.toUpperCase()),
        );
      })
      .map((cls) => cls.id);
  }, [selectedGrades, classesData]);

  // Fetch global metrics to calculate KPI cards
  const isAll = activeClassIds.length === 0;

  // Render hidden fetchers to aggregate global data across selected classes
  const [globalClassDataMap, setGlobalClassDataMap] = useState({});
  useEffect(() => {
    setGlobalClassDataMap({});
  }, [activeClassIds, startDate, endDate]);

  const handleGlobalClassLoaded = (id, data) => {
    setGlobalClassDataMap((prev) => ({ ...prev, [id]: data }));
  };

  // Calculate previous period
  const currentDuration = dateRange.end.diff(dateRange.start, "day");
  const prevStart = dateRange.start.subtract(currentDuration + 1, "day");
  const prevEnd = dateRange.start.subtract(1, "day");
  const prevStartDate = prevStart.format("YYYY-MM-DD");
  const prevEndDate = prevEnd.format("YYYY-MM-DD");

  const { data: globalData, isLoading: isGlobalLoading } =
    useGetActivitiesGlobalStatsQuery(
      { start_date: startDate, end_date: endDate },
      { skip: !isAll },
    );

  // Fetch previous period data
  const { data: prevGlobalData, isLoading: isPrevGlobalLoading } =
    useGetActivitiesGlobalStatsQuery(
      { start_date: prevStartDate, end_date: prevEndDate },
      { skip: !isAll },
    );

  const globalAggregated = useMemo(() => {
    if (isAll) {
      return globalData;
    }

    let total_activities = 0;
    const activities_by_type = {};

    activeClassIds.forEach((id) => {
      const data = globalClassDataMap[id];
      if (!data) return;

      total_activities += data.total_activities || 0;
      const byType = data.activities_by_type || {};

      Object.keys(byType).forEach((type) => {
        if (!activities_by_type[type]) {
          activities_by_type[type] = {
            name: byType[type].name,
            count: 0,
            percentage: 0,
          };
        }
        activities_by_type[type].count += byType[type].count || 0;
      });
    });

    Object.keys(activities_by_type).forEach((type) => {
      const item = activities_by_type[type];
      item.percentage =
        total_activities > 0
          ? parseFloat(((item.count / total_activities) * 100).toFixed(2))
          : 0;
    });

    return {
      total_activities,
      activities_by_type,
    };
  }, [isAll, globalData, globalClassDataMap, activeClassIds]);

  // Calculate comparison with previous period
  const comparisonWithPrevious = useMemo(() => {
    const currentTotal = globalAggregated?.total_activities || 0;
    const prevTotal = isAll ? prevGlobalData?.total_activities || 0 : 0;

    if (prevTotal === 0) {
      return currentTotal > 0 ? 100 : 0; // 100% increase if previous was 0
    }

    const percentageChange = Math.round(
      ((currentTotal - prevTotal) / prevTotal) * 100,
    );
    return percentageChange;
  }, [globalAggregated, prevGlobalData, isAll]);

  const isGlobalLoadingState = isAll
    ? isGlobalLoading || isPrevGlobalLoading
    : activeClassIds.some((id) => !globalClassDataMap[id]);

  // Compute dominant feature
  const dominantFeature = useMemo(() => {
    if (!globalAggregated?.activities_by_type)
      return { name: "Aucune", percentage: 0 };
    const byType = globalAggregated.activities_by_type;
    let maxKey = null;
    let maxCount = -1;

    Object.keys(byType).forEach((key) => {
      if (byType[key].count > maxCount) {
        maxCount = byType[key].count;
        maxKey = key;
      }
    });

    if (!maxKey) return { name: "Aucune", percentage: 0 };
    return {
      name: byType[maxKey].name || maxKey,
      percentage:
        globalAggregated.total_activities > 0
          ? Math.round(
              (byType[maxKey].count / globalAggregated.total_activities) * 100,
            )
          : 0,
    };
  }, [globalAggregated]);

  // Compute Aha Candidate
  const ahaCandidate = useMemo(() => {
    const combinedCounts = {};
    let total = 0;

    const aggregateCategory = (key) => {
      const data = categoryDataMap[key];
      if (!data?.activities_by_type) return;
      Object.keys(data.activities_by_type).forEach((typeKey) => {
        const item = data.activities_by_type[typeKey];
        if (!combinedCounts[typeKey]) {
          combinedCounts[typeKey] = { name: item.name, count: 0 };
        }
        combinedCounts[typeKey].count += item.count || 0;
        total += item.count || 0;
      });
    };

    aggregateCategory("USER");
    aggregateCategory("USER_ACTIVE");

    let maxKey = null;
    let maxCount = -1;

    Object.keys(combinedCounts).forEach((key) => {
      if (combinedCounts[key].count > maxCount) {
        maxCount = combinedCounts[key].count;
        maxKey = key;
      }
    });

    if (!maxKey || maxCount === 0) {
      return { name: "Aucune", percentage: 0 };
    }

    return {
      name: combinedCounts[maxKey].name || maxKey,
      percentage: total > 0 ? Math.round((maxCount / total) * 100) : 0,
    };
  }, [categoryDataMap]);

  const handleToggleGrade = (grade) => {
    setSelectedGrades((prev) => {
      const next = new Set(prev);
      if (next.has(grade)) {
        next.delete(grade);
      } else {
        next.add(grade);
      }
      return next;
    });
  };

  const handleClearGrades = () => {
    setSelectedGrades(new Set());
  };

  return (
    <Box mt="xl">
      {/* Dynamic Loaders for Global Aggregates */}
      {!isAll &&
        activeClassIds.map((id) => (
          <ClassDataFetcher
            key={`global-${id}-${startDate}-${endDate}`}
            classeId={id}
            startDate={startDate}
            endDate={endDate}
            onLoaded={(data) => handleGlobalClassLoaded(id, data)}
          />
        ))}

      {/* Header and Subtitles */}
      <Box mb="md">
        <Group spacing="sm" align="center">
          <ThemeIcon variant="light" color="blue" size={32} radius="md">
            <Activity size={20} />
          </ThemeIcon>
          <Title
            order={3}
            sx={{ fontSize: "20px", fontWeight: 700, color: "#1c7ed6" }}
          >
            Vue 1 — Funnel × Feature
          </Title>
        </Group>
        <Text size="sm" color="dimmed" mt={4}>
          Quelle fonctionnalité utilise chaque étape du funnel ? (% des
          activités dans le segment)
        </Text>
      </Box>

      {/* Class Selector Pill Filters */}
      <Paper
        p="md"
        radius="lg"
        shadow="sm"
        withBorder
        mb="lg"
        sx={{ backgroundColor: "#ffffff" }}
      >
        <Text
          size="xs"
          weight={700}
          color="dimmed"
          transform="uppercase"
          mb="xs"
          letterSpacing={0.5}
        >
          Classe — filtre global, s'applique aux 3 vues - multi-sélection
        </Text>
        <Group spacing="xs">
          <Button
            size="xs"
            radius="xl"
            variant={selectedGrades.size === 0 ? "filled" : "light"}
            color={selectedGrades.size === 0 ? "dark" : "gray"}
            onClick={handleClearGrades}
            styles={{
              root: {
                fontWeight: 600,
                backgroundColor:
                  selectedGrades.size === 0 ? "#1a202c" : "#f1f3f5",
                color: selectedGrades.size === 0 ? "#ffffff" : "#495057",
                "&:hover": {
                  backgroundColor:
                    selectedGrades.size === 0 ? "#2d3748" : "#e9ecef",
                },
              },
            }}
          >
            Toutes
          </Button>

          {BASE_GRADES.map((grade) => {
            const isActive = selectedGrades.has(grade);
            return (
              <Button
                key={grade}
                size="xs"
                radius="xl"
                variant={isActive ? "filled" : "outline"}
                color={isActive ? "blue" : "gray"}
                onClick={() => handleToggleGrade(grade)}
                leftIcon={
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: isActive ? "#ffffff" : "#adb5bd",
                    }}
                  />
                }
                styles={{
                  root: {
                    fontWeight: 600,
                    borderColor: isActive ? "#3b82f6" : "#dee2e6",
                    backgroundColor: isActive ? "#3b82f6" : "#ffffff",
                    color: isActive ? "#ffffff" : "#495057",
                    "&:hover": {
                      backgroundColor: isActive ? "#2563eb" : "#f8f9fa",
                    },
                  },
                }}
              >
                {grade}
              </Button>
            );
          })}
        </Group>
      </Paper>

      {/* KPI Cards Section */}
      <SimpleGrid
        cols={3}
        spacing="lg"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
        mb="xl"
        sx={{ maxWidth: "900px", marginLeft: "auto", marginRight: "auto" }}
      >
        <Card
          p="md"
          radius="lg"
          shadow="xs"
          withBorder
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            minHeight: "100px",
          }}
        >
          <Text
            size="xs"
            color="dimmed"
            transform="uppercase"
            weight={700}
            letterSpacing={0.5}
            align="center"
          >
            Activités Totales (S)
          </Text>
          {isGlobalLoadingState ? (
            <Loader size="sm" mt="sm" />
          ) : (
            <Group position="center" align="center" mt="sm" spacing="md">
              <Text size={28} weight={800} sx={{ lineHeight: 1.1 }}>
                {globalAggregated?.total_activities || 0}
              </Text>
              <Group
                spacing={4}
                sx={{
                  color: comparisonWithPrevious >= 0 ? "#10b981" : "#fa5252",
                }}
              >
                <TrendingUp size={16} />
                <Text size="xs" weight={700}>
                  {comparisonWithPrevious >= 0 ? "+" : ""}
                  {comparisonWithPrevious}% vs S-1
                </Text>
              </Group>
            </Group>
          )}
        </Card>

        <Card
          p="md"
          radius="lg"
          shadow="xs"
          withBorder
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            minHeight: "100px",
          }}
        >
          <Text
            size="xs"
            color="dimmed"
            transform="uppercase"
            weight={700}
            letterSpacing={0.5}
            align="center"
          >
            Feature Dominante
          </Text>
          {isGlobalLoadingState ? (
            <Loader size="sm" mt="sm" />
          ) : (
            <Box mt="sm">
              <Text
                size={24}
                weight={800}
                color="grape"
                sx={{ lineHeight: 1.1 }}
                align="center"
              >
                {dominantFeature.name}
              </Text>
              <Text size="xs" color="dimmed" weight={500} mt={4} align="center">
                {dominantFeature.percentage}% des activités
              </Text>
            </Box>
          )}
        </Card>

        <Card
          p="md"
          radius="lg"
          shadow="xs"
          withBorder
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            minHeight: "100px",
          }}
        >
          <Text
            size="xs"
            color="dimmed"
            transform="uppercase"
            weight={700}
            letterSpacing={0.5}
            align="center"
          >
            Aha Candidate
          </Text>
          {isGlobalLoadingState ? (
            <Loader size="sm" mt="sm" />
          ) : (
            <Box mt="sm">
              <Text
                size={24}
                weight={800}
                color="teal"
                sx={{ lineHeight: 1.1 }}
                align="center"
              >
                {ahaCandidate.name}
              </Text>
              <Text size="xs" color="dimmed" weight={500} mt={4} align="center">
                {ahaCandidate.percentage}% des act. avant client
              </Text>
            </Box>
          )}
        </Card>
      </SimpleGrid>

      {/* Grid Heat-map Table */}
      {errorClasses ? (
        <Alert icon={<AlertCircle size={16} />} title="Erreur" color="red">
          Impossible de charger la liste des classes.
        </Alert>
      ) : (
        <Paper
          radius="lg"
          shadow="sm"
          withBorder
          sx={{ overflow: "hidden", backgroundColor: "#ffffff" }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "800px",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontSize: "12px",
                      color: "#495057",
                      fontWeight: 800,
                    }}
                  >
                    ÉTAPE FUNNEL
                  </th>
                  {COLUMN_CONFIG.map((col) => {
                    const displayName =
                      globalAggregated?.activities_by_type?.[
                        col.key
                      ]?.name?.toUpperCase() || col.label;
                    return (
                      <th
                        key={col.key}
                        style={{
                          padding: "16px",
                          textAlign: "center",
                          fontSize: "12px",
                          color: col.getBgColor(100),
                          fontWeight: 800,
                        }}
                      >
                        {displayName}
                      </th>
                    );
                  })}
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "center",
                      fontSize: "12px",
                      color: "#1a202c",
                      fontWeight: 800,
                      width: "100px",
                    }}
                  >
                    TOTAL
                  </th>
                </tr>
              </thead>
              <tbody>
                {CATEGORIES.map((category) => (
                  <StatsRow
                    key={category.key}
                    category={category}
                    activeClassIds={activeClassIds}
                    startDate={startDate}
                    endDate={endDate}
                    activeFunnelCounts={activeFunnelCounts}
                    onDataLoaded={handleCategoryDataLoaded}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}
    </Box>
  );
}
